import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadSection } from "@/components/LeadSection";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer Pricing: Hourly and Fixed Scope";
const DESCRIPTION =
  "Shopify development at €110 net per hour, new stores from around €3,000 and conversion work from €3,990 net. Scope and cost are agreed before work starts.";

export const Route = createFileRoute("/en/pricing")({
  component: PricingEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en/pricing` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/pricing` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/preise" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/pricing" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/preise" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/preise` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/pricing` },
    ],
  }),
});

const tiers = [
  {
    name: "Conversion Sprint",
    price: "€3,990",
    for: "Stores with steady traffic and one clear problem area",
    items: [
      "Conversion audit with prioritised findings",
      "Implementation of the highest-impact changes",
      "Product page and checkout work",
      "Before/after measurement on the same metrics",
    ],
  },
  {
    name: "Conversion Programme",
    price: "€9,900",
    for: "Stores where several areas need work at once",
    featured: true,
    items: [
      "Everything in the Sprint",
      "Page speed and mobile overhaul",
      "A/B testing where traffic allows",
      "Three months of iteration and review",
    ],
  },
  {
    name: "Partnership",
    price: "€18,900",
    for: "Growing stores that want continuous work on the number",
    items: [
      "Everything in the Programme",
      "Continuous testing roadmap",
      "Migrations and larger rebuilds included",
      "Direct availability for decisions",
    ],
  },
];

function PricingEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Pricing
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
               Clear scope and cost <span className="text-signal">before work starts</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
               Theme changes and development are €110 net per hour with an estimate in advance.
               Small new stores start at around €3,000 net. The options below are for conversion work.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-6 px-5 md:grid-cols-3">
            {tiers.map((t) => (
              <div
                key={t.name}
                className={`rounded-lg border bg-card p-6 ${
                  t.featured ? "border-primary/50 shadow-lift" : "border-border"
                }`}
              >
                <h2 className="text-lg font-semibold">{t.name}</h2>
                <p className="mt-3 font-display text-3xl font-bold">{t.price}</p>
                <p className="mt-2 text-sm text-muted-foreground">{t.for}</p>
                <ul className="mt-5 grid gap-2 text-sm">
                  {t.items.map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {i}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/en/contact"
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
                >
                  Get a free assessment <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-12 max-w-3xl px-5">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Not sure which one fits? Run your numbers through the{" "}
              <Link to="/en/roi-calculator" className="text-primary underline underline-offset-4">
                conversion calculator
              </Link>{" "}
              first. If the maths does not support the investment, I will tell you before we start.
            </p>
          </div>
        </section>

        <LeadSection source="en-pricing" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
